import moment from "moment/moment";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import { SCHEDULE_DATA_PER_PAGE } from "@/components/list/Schedule/constants";
import { ScheduleSchedule, SearchInput } from "@/components/list/Schedule/types";
import { ScheduleSchedulesRequest } from "@/components/list/Schedule/types/api";
import { calculateStartPos } from "@/lib/pager";
import { replaceQueryParams } from "@/lib/urlHelper";
import { RootState } from "@/reducers/types";

type SearchOption = {
  page?: number;
  isPartialUpdate?: boolean;
  preventRefresh?: boolean;
};

export const usePlanList = (): {
  search: (input: SearchInput, option?: SearchOption) => void;
  prev: () => void;
  next: () => void;
  jump: (page) => void;
} => {
  const dispatch = useDispatch();

  const { requestForScheduleList, currentPageOfScheduleList } = useSelector((state: RootState) => state.schedule);

  const toRequest = (input: SearchInput): Omit<ScheduleSchedulesRequest, "start" | "limit"> => {
    return {
      schedule_date: moment(input.schedule_date).format("YYYY-MM-DD"),
      category_id: input.category_id,
      area_id: input.area_id,
      device_id: input.device_id,
      manager_user_id: input.manager_user_id,
      field3: input.field3,
      field1: input.field1,
      field2: input.field2,
      approve1_user_id: input.approve1_user_id,
      approve4_user_id: input.approve4_user_id,
      field_t9: input.field_t9,
      filter: [input.approve1_unprocessed ? 1 : null, input.approve4_unprocessed ? 2 : null].filter((v) => v),
    };
  };

  const search = (input: SearchInput, option?: SearchOption) => {
    const p = option?.page ?? 1;

    if (option?.isPartialUpdate) {
      dispatch(
        actions.schedule.getAndUpdateScheduleOvertime({
          ...toRequest(input),
          start: (p - 1) * SCHEDULE_DATA_PER_PAGE + 1,
          limit: SCHEDULE_DATA_PER_PAGE,
        })
      );
    } else {
      replaceQueryParams({ ps: p });
      const params = {
        ...toRequest(input),
        start: calculateStartPos(p, SCHEDULE_DATA_PER_PAGE),
        limit: SCHEDULE_DATA_PER_PAGE,
      };
      dispatch(
        actions.schedule.getScheduleSchedules(params, p, option?.preventRefresh, (res: ScheduleSchedule) => {
          if (res.list.length === 0 && 0 < res.total_num && res.total_num < params.start) {
            const maxPage = Math.ceil(res.total_num / SCHEDULE_DATA_PER_PAGE);
            const fixedParams = { ...params, start: calculateStartPos(maxPage, SCHEDULE_DATA_PER_PAGE) };
            replaceQueryParams({ ps: maxPage });
            dispatch(actions.schedule.getScheduleSchedules(fixedParams, maxPage, option?.preventRefresh));
          }
        })
      );
    }
  };

  const prev = () => {
    const p = currentPageOfScheduleList - 1;
    replaceQueryParams({ ps: p });
    dispatch(
      actions.schedule.getScheduleSchedules(
        {
          ...requestForScheduleList,
          start: calculateStartPos(p, SCHEDULE_DATA_PER_PAGE),
        },
        p
      )
    );
  };

  const next = () => {
    const p = currentPageOfScheduleList + 1;
    replaceQueryParams({ ps: p });
    dispatch(
      actions.schedule.getScheduleSchedules(
        {
          ...requestForScheduleList,
          start: calculateStartPos(p, SCHEDULE_DATA_PER_PAGE),
        },
        p
      )
    );
  };

  const jump = (page) => {
    replaceQueryParams({ ps: page });
    dispatch(
      actions.schedule.getScheduleSchedules(
        {
          ...requestForScheduleList,
          start: calculateStartPos(page, SCHEDULE_DATA_PER_PAGE),
        },
        page
      )
    );
  };

  return { search, prev, next, jump };
};
